import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import { Building2, User, Target, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskDialog = ({ open, onOpenChange, companyId, contactId, opportunityId, typsaOrgUnitId, task, onSuccess }) => {
  const { currentUser } = useUser();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    status: 'Open',
    priority: 'medium',
    company_id: companyId || '',
    contact_id: contactId || '',
    opportunity_id: opportunityId || '',
    typsa_org_unit_id: typsaOrgUnitId || '',
    owner: '',
    assignee_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  
  // Company type selection for cascading filters
  const [companyType, setCompanyType] = useState(''); // '', 'typsa', 'privada', 'publica'
  const [typsaCategory, setTypsaCategory] = useState(''); // division, departamento, territorio, grupo

  // Fetch all reference data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, companiesRes, contactsRes, oppsRes, typsaRes] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/companies`),
          axios.get(`${API}/contacts`),
          axios.get(`${API}/opportunities`),
          axios.get(`${API}/typsa-org-units`)
        ]);
        setUsers(usersRes.data);
        setCompanies(companiesRes.data);
        setContacts(contactsRes.data);
        setOpportunities(oppsRes.data);
        setTypsaOrgUnits(typsaRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    if (open) {
      fetchData();
    }
  }, [open]);

  // Initialize form data when dialog opens or task changes
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date || '',
        status: task.status || 'Open',
        priority: task.priority || 'medium',
        company_id: task.company_id || companyId || '',
        contact_id: task.contact_id || contactId || '',
        opportunity_id: task.opportunity_id || opportunityId || '',
        typsa_org_unit_id: task.typsa_org_unit_id || typsaOrgUnitId || '',
        owner: task.owner || '',
        assignee_email: task.assignee_email || ''
      });
      
      // Set company type based on existing data
      if (task.typsa_org_unit_id) {
        setCompanyType('typsa');
        const unit = typsaOrgUnits.find(u => u.id === task.typsa_org_unit_id);
        if (unit) setTypsaCategory(unit.category || '');
      } else if (task.company_id) {
        const company = companies.find(c => c.id === task.company_id);
        if (company) {
          setCompanyType(company.company_type || '');
        }
      }
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: '',
        status: 'Open',
        priority: 'medium',
        company_id: companyId || '',
        contact_id: contactId || '',
        opportunity_id: opportunityId || '',
        typsa_org_unit_id: typsaOrgUnitId || '',
        owner: currentUser?.name || '',
        assignee_email: currentUser?.email || ''
      });
      
      // Set initial company type from props
      if (typsaOrgUnitId) {
        setCompanyType('typsa');
      } else if (companyId) {
        const company = companies.find(c => c.id === companyId);
        if (company) setCompanyType(company.company_type || '');
      } else {
        setCompanyType('');
        setTypsaCategory('');
      }
    }
  }, [task, companyId, contactId, opportunityId, typsaOrgUnitId, open, currentUser, companies, typsaOrgUnits]);

  // Handle user selection - sets both owner (name) and assignee_email
  const handleUserSelect = (userEmail) => {
    if (userEmail === 'none') {
      setFormData({ ...formData, owner: '', assignee_email: '' });
      return;
    }
    const selectedUser = users.find(u => u.email === userEmail);
    if (selectedUser) {
      setFormData({
        ...formData,
        owner: selectedUser.name,
        assignee_email: selectedUser.email
      });
    }
  };

  // Get current selected user email based on owner name or assignee_email
  const getSelectedUserEmail = () => {
    if (!formData.owner && !formData.assignee_email) return 'none';
    const user = users.find(u => u.name === formData.owner || u.email === formData.assignee_email);
    return user?.email || 'none';
  };

  // Handle company type change
  const handleCompanyTypeChange = (type) => {
    setCompanyType(type);
    setTypsaCategory('');
    setFormData({
      ...formData,
      company_id: '',
      typsa_org_unit_id: '',
      contact_id: '',
      opportunity_id: ''
    });
  };

  // Handle TYPSA category change
  const handleTypsaCategoryChange = (category) => {
    setTypsaCategory(category);
    setFormData({
      ...formData,
      typsa_org_unit_id: '',
      contact_id: '',
      opportunity_id: ''
    });
  };

  // Handle company selection
  const handleCompanyChange = (companyId) => {
    if (companyId === 'none') {
      setFormData({ ...formData, company_id: '', contact_id: '', opportunity_id: '' });
      return;
    }
    setFormData({
      ...formData,
      company_id: companyId,
      contact_id: '',
      opportunity_id: ''
    });
  };

  // Handle TYPSA org unit selection
  const handleTypsaUnitChange = (unitId) => {
    if (unitId === 'none') {
      setFormData({ ...formData, typsa_org_unit_id: '', contact_id: '', opportunity_id: '' });
      return;
    }
    setFormData({
      ...formData,
      typsa_org_unit_id: unitId,
      company_id: '', // Clear company when selecting TYPSA unit
      contact_id: '',
      opportunity_id: ''
    });
  };

  // Get filtered companies by type
  const getFilteredCompanies = () => {
    if (!companyType || companyType === 'typsa') return [];
    return companies.filter(c => c.company_type === companyType);
  };

  // Get filtered TYPSA org units by category
  const getFilteredTypsaUnits = () => {
    if (!typsaCategory) return [];
    return typsaOrgUnits.filter(u => u.category === typsaCategory);
  };

  // Get filtered contacts by company or TYPSA unit
  const getFilteredContacts = () => {
    if (formData.typsa_org_unit_id) {
      return contacts.filter(c => c.typsa_org_unit_id === formData.typsa_org_unit_id);
    }
    if (formData.company_id) {
      return contacts.filter(c => c.company_id === formData.company_id);
    }
    return [];
  };

  // Get filtered opportunities by company or TYPSA unit
  const getFilteredOpportunities = () => {
    if (formData.typsa_org_unit_id) {
      return opportunities.filter(o => o.typsa_org_unit_id === formData.typsa_org_unit_id);
    }
    if (formData.company_id) {
      return opportunities.filter(o => o.company_id === formData.company_id);
    }
    return [];
  };

  // Get display name for selected entity
  const getCompanyDisplayName = () => {
    if (formData.typsa_org_unit_id) {
      const unit = typsaOrgUnits.find(u => u.id === formData.typsa_org_unit_id);
      return unit ? `TYPSA: ${unit.value || unit.name}` : '';
    }
    if (formData.company_id) {
      const company = companies.find(c => c.id === formData.company_id);
      return company?.name || '';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('El título de la tarea es obligatorio');
      return;
    }

    setLoading(true);
    try {
      const headers = { 'x-user-email': currentUser?.email };
      
      // Clean up empty strings to null
      const payload = {
        ...formData,
        company_id: formData.company_id || null,
        contact_id: formData.contact_id || null,
        opportunity_id: formData.opportunity_id || null,
        typsa_org_unit_id: formData.typsa_org_unit_id || null,
        description: formData.description || null
      };
      
      if (task) {
        await axios.put(`${API}/tasks/${task.id}`, payload, { headers });
        toast.success('Tarea actualizada correctamente');
      } else {
        await axios.post(`${API}/tasks`, payload, { headers });
        toast.success('Tarea creada correctamente');
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Error al guardar la tarea');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = getFilteredContacts();
  const filteredOpportunities = getFilteredOpportunities();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>{task ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Title - Required */}
            <div>
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Describe la tarea..."
                data-testid="task-title-input"
                required
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detalles adicionales de la tarea..."
                rows={3}
                data-testid="task-description-input"
              />
            </div>

            {/* Priority, Due Date, Status Row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="priority">Prioridad</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Alta
                      </span>
                    </SelectItem>
                    <SelectItem value="medium">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                        Media
                      </span>
                    </SelectItem>
                    <SelectItem value="low">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Baja
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="due_date">Fecha Vencimiento</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="task-date-input"
                />
              </div>
              <div>
                <Label htmlFor="status">Estado</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Pendiente</SelectItem>
                    <SelectItem value="Completed">Completada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Responsible */}
            <div>
              <Label htmlFor="owner">Responsable</Label>
              <Select 
                value={getSelectedUserEmail()} 
                onValueChange={handleUserSelect}
              >
                <SelectTrigger data-testid="task-owner-select">
                  <SelectValue placeholder="Seleccionar responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.email} value={user.email}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Company/Entity Selection - Collapsible */}
            <Accordion type="single" collapsible defaultValue={formData.company_id || formData.typsa_org_unit_id ? "entity" : ""}>
              <AccordionItem value="entity" className="border rounded-lg px-4">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>Vinculación</span>
                    {getCompanyDisplayName() && (
                      <span className="text-xs text-muted-foreground ml-2">
                        ({getCompanyDisplayName()})
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  {/* Company Type Selection */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo de Organización</Label>
                    <Select value={companyType} onValueChange={handleCompanyTypeChange}>
                      <SelectTrigger data-testid="task-company-type-select">
                        <SelectValue placeholder="Seleccionar tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin vincular</SelectItem>
                        <SelectItem value="typsa">TYPSA (interno)</SelectItem>
                        <SelectItem value="privada">Empresa Privada</SelectItem>
                        <SelectItem value="publica">Administración Pública</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* TYPSA Category - only if TYPSA selected */}
                  {companyType === 'typsa' && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Categoría TYPSA</Label>
                      <Select value={typsaCategory} onValueChange={handleTypsaCategoryChange}>
                        <SelectTrigger data-testid="task-typsa-category-select">
                          <SelectValue placeholder="Seleccionar categoría..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="division">División</SelectItem>
                          <SelectItem value="departamento">Departamento</SelectItem>
                          <SelectItem value="territorio">Territorio</SelectItem>
                          <SelectItem value="grupo">Grupo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* TYPSA Org Unit Selection */}
                  {companyType === 'typsa' && typsaCategory && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Unidad TYPSA</Label>
                      <Select 
                        value={formData.typsa_org_unit_id || 'none'} 
                        onValueChange={handleTypsaUnitChange}
                      >
                        <SelectTrigger data-testid="task-typsa-unit-select">
                          <SelectValue placeholder="Seleccionar unidad..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin seleccionar</SelectItem>
                          {getFilteredTypsaUnits().map((unit) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              {unit.value || unit.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Company Selection - for non-TYPSA */}
                  {(companyType === 'privada' || companyType === 'publica') && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Empresa</Label>
                      <Select 
                        value={formData.company_id || 'none'} 
                        onValueChange={handleCompanyChange}
                      >
                        <SelectTrigger data-testid="task-company-select">
                          <SelectValue placeholder="Seleccionar empresa..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin seleccionar</SelectItem>
                          {getFilteredCompanies().map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Contact Selection - filtered by company/unit */}
                  {(formData.company_id || formData.typsa_org_unit_id) && (
                    <div>
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Contacto relacionado
                      </Label>
                      <Select 
                        value={formData.contact_id || 'none'} 
                        onValueChange={(v) => setFormData({ ...formData, contact_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger data-testid="task-contact-select">
                          <SelectValue placeholder="Seleccionar contacto..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin contacto</SelectItem>
                          {filteredContacts.length === 0 ? (
                            <SelectItem value="no-contacts" disabled>
                              No hay contactos disponibles
                            </SelectItem>
                          ) : (
                            filteredContacts.map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.first_name || contact.name} {contact.last_name || ''}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Opportunity Selection - filtered by company/unit */}
                  {(formData.company_id || formData.typsa_org_unit_id) && (
                    <div>
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Oportunidad relacionada
                      </Label>
                      <Select 
                        value={formData.opportunity_id || 'none'} 
                        onValueChange={(v) => setFormData({ ...formData, opportunity_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger data-testid="task-opportunity-select">
                          <SelectValue placeholder="Seleccionar oportunidad..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin oportunidad</SelectItem>
                          {filteredOpportunities.length === 0 ? (
                            <SelectItem value="no-opps" disabled>
                              No hay oportunidades disponibles
                            </SelectItem>
                          ) : (
                            filteredOpportunities.map((opp) => (
                              <SelectItem key={opp.id} value={opp.id}>
                                {opp.title || opp.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-task-btn">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-task-btn">
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDialog;
